import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import * as Sentry from "@sentry/react";
import { useLoginMutation } from "@/apis/oneclub/hooks/members";
import { useCheckTeacherOrEditorQuery } from "@/apis/naniexpert/hooks/member";
import { ApiError } from "@/apis/naniexpert/ApiError";
import { useUserProfileQuery } from "@/apis/userProfile";
import { deleteToken, getToken, setToken } from "@/utils/auth/helpers";
import { consumeNaniClubTokenFromUrl } from "@/utils/auth/naniClubOidc";
import { AuthContext } from "./authContext";

const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [token, setTokenState] = useState<string | null>(
    () => consumeNaniClubTokenFromUrl() ?? getToken()
  );
  const loginMutation = useLoginMutation();

  const {
    data: user,
    refetch: refetchGetMe,
    isPending,
  } = useUserProfileQuery(token);

  const { error: identityError } = useCheckTeacherOrEditorQuery({
    jwt: token || "",
    enabled: !!token,
  });

  // 有權限時，code = 200
  // 沒有權限時，code = 403
  const isForbidden =
    identityError instanceof ApiError && identityError.code === 403;
  const forbiddenMessage = isForbidden ? identityError.message : "";

  // 同步使用者資訊到 Sentry 紀錄發生異常時的使用者資訊，登出時清除
  useEffect(() => {
    if (user) {
      Sentry.setUser({ username: user.fullName });
    } else {
      Sentry.setUser(null);
    }
  }, [user]);

  const logout = useCallback(() => {
    deleteToken();
    setTokenState(null);
  }, []);

  const getMe = useCallback(async () => {
    const currentToken = getToken();
    if (!currentToken) {
      return;
    }
    setTokenState(currentToken);
    try {
      await refetchGetMe();
    } catch {
      logout();
      throw new Error("取得使用者資訊失敗");
    }
  }, [refetchGetMe, logout]);

  const login = useCallback(
    async (username: string, password: string) => {
      try {
        const { jwt } = await loginMutation.mutateAsync({
          username,
          password,
          from: "Nani",
        });
        setToken(jwt);
        setTokenState(jwt);
        // token 更新後，useUserProfileQuery 會自動觸發（因為 enabled 變為 true）
      } catch {
        throw new Error("登入失敗：帳號不存在或密碼錯誤");
      }
    },
    [loginMutation]
  );

  // 計算 isUninitialized：有 token 且正在載入且還沒有 user 資料時為 true
  // 沒有 token 時為 false（已經初始化，只是沒有使用者）
  const isUninitialized = token ? isPending && !user : false;

  const contextValue = useMemo(
    () => ({
      user: user || null,
      login,
      logout,
      getMe,
      isUninitialized,
      isForbidden,
      forbiddenMessage,
    }),
    [user, login, logout, getMe, isUninitialized, isForbidden, forbiddenMessage]
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export default AuthProvider;
